import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Loader2, Clock, ArrowUpRight,
  Shield, FileSearch, CheckCircle2,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { profilerApi } from '@/api/profiler';
import { RiskTierBadge } from '@/components/profiler/RiskTierBadge';
import type { SeedType, RiskTier, CDDReport } from '@/types/profiler';
import { SEED_LABELS } from '@/types/profiler';

// ── Constants ─────────────────────────────────────────────────────────────────

const SEED_TYPES: { value: SeedType | 'AUTO'; label: string }[] = [
  { value: 'AUTO', label: 'Auto-detect' },
  { value: 'NAME', label: 'Name' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'PAN', label: 'PAN' },
  { value: 'COMPANY', label: 'Company' },
  { value: 'DOMAIN', label: 'Domain' },
  { value: 'IP', label: 'IP' },
];

const JURISDICTIONS = [
  { value: 'india_gulf', label: 'India + Gulf' },
  { value: 'india', label: 'India Only' },
  { value: 'gulf_mena', label: 'Gulf MENA' },
  { value: 'global', label: 'Global' },
];

const MODULE_CONFIG: {
  key: string;
  label: string;
  desc: string;
  defaultOn: boolean;
}[] = [
  { key: 'sanctions',   label: 'Sanctions',          desc: 'OFAC, UN, EU, India MEA lists',          defaultOn: true  },
  { key: 'pep',         label: 'PEP / UBO',          desc: 'Politically exposed persons + ownership', defaultOn: true  },
  { key: 'adverse_media', label: 'Adverse Media',    desc: 'News & press OSINT scoring',             defaultOn: true  },
  { key: 'enforcement', label: 'India Enforcement',  desc: 'ED, CBI, SEBI enforcement orders',       defaultOn: true  },
  { key: 'ato',         label: 'ATO / Breach',       desc: 'Account takeover & dark-web exposure',   defaultOn: true  },
  { key: 'court',       label: 'eCourts',            desc: 'Supreme Court + HC case lookup',         defaultOn: true  },
  { key: 'social',      label: 'Social Discovery',   desc: 'Cross-platform username / email probe',  defaultOn: false },
  { key: 'crypto',      label: 'Crypto / WazirX',    desc: 'On-chain + exchange KYC flags',          defaultOn: false },
  { key: 'wealth',      label: 'Wealth Signals',     desc: 'UPI, property, vehicle asset mapping',   defaultOn: false },
  { key: 'corporate',   label: 'India Corporate',    desc: 'MCA21 directorship & ROC filings',       defaultOn: false },
];

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

function relativeTime(iso?: string): string {
  if (!iso) return '—';
  const delta = Date.now() - new Date(iso).getTime();
  const s = Math.floor(delta / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-4 py-3 animate-pulse" style={{ borderBottom: '1px solid #182030' }}>
      <div style={{ width: 140, height: 10, background: '#182030', borderRadius: 4 }} />
      <div style={{ width: 48, height: 18, background: '#182030', borderRadius: 20 }} />
      <div style={{ width: 56, height: 18, background: '#182030', borderRadius: 20 }} />
      <div style={{ width: 40, height: 18, background: '#182030', borderRadius: 20 }} />
      <div style={{ width: 64, height: 10, background: '#182030', borderRadius: 4, marginLeft: 'auto' }} />
    </div>
  );
}

// ── Seed type chip ────────────────────────────────────────────────────────────

function SeedChip({ seedType }: { seedType?: string }) {
  const colors: Record<string, string> = {
    NAME: '#00C8FF', EMAIL: '#2979FF', PHONE: '#00C853', PAN: '#00C853',
    COMPANY: '#F0B429', DOMAIN: '#F0B429', IP: '#FF6B1A',
    USERNAME: '#00C8FF', DEFAULT: '#5A7A9A',
  };
  const c = colors[seedType ?? ''] ?? colors.DEFAULT;
  const label = seedType ? (SEED_LABELS[seedType] ?? seedType) : 'UNKNOWN';
  return (
    <span
      style={{ background: `${c}18`, color: c, border: `1px solid ${c}30` }}
      className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full tracking-wider"
    >
      {label}
    </span>
  );
}

// ── Case type badge ───────────────────────────────────────────────────────────

function CaseTypeBadge({ type }: { type?: string }) {
  const isCDD = type !== 'EDD';
  return (
    <span
      style={{
        background: isCDD ? '#001A2E' : '#1A0030',
        color: isCDD ? '#00C8FF' : '#A855F7',
        border: `1px solid ${isCDD ? '#00C8FF30' : '#A855F730'}`,
      }}
      className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full tracking-wider"
    >
      {isCDD ? 'CDD' : 'EDD'}
    </span>
  );
}

// ── Module Toggle Grid ────────────────────────────────────────────────────────

function ModuleToggleGrid({
  modules,
  onChange,
}: {
  modules: Record<string, boolean>;
  onChange: (key: string, val: boolean) => void;
}) {
  return (
    <div>
      <p
        className="text-[9px] font-mono tracking-[0.18em] mb-3"
        style={{ color: '#5A7A9A' }}
      >
        MODULE CONFIGURATION
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {MODULE_CONFIG.map((m) => (
          <div
            key={m.key}
            style={{
              background: '#0C1118',
              border: `1px solid ${modules[m.key] ? '#1E2B3F' : '#182030'}`,
              borderRadius: 8,
            }}
            className="flex items-center justify-between px-3 py-2.5 gap-3"
          >
            <div className="flex flex-col gap-0.5 min-w-0">
              <span style={{ color: modules[m.key] ? '#DCE8F5' : '#5A7A9A' }} className="text-[11px] font-semibold truncate">
                {m.label}
              </span>
              <span style={{ color: '#3A5A7A' }} className="text-[9px] font-mono truncate">
                {m.desc}
              </span>
            </div>
            <Switch
              checked={modules[m.key]}
              onCheckedChange={(v) => onChange(m.key, v)}
              className="flex-shrink-0"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Scanning overlay ──────────────────────────────────────────────────────────

const SCAN_STEPS = [
  { label: 'Resolving entity identifiers',     detail: 'Breach Intelligence / OSINT graph'  },
  { label: 'Querying OpenSanctions',           detail: 'UNSC · OFAC SDN · EU · India MEA'  },
  { label: 'Checking PEP registry',            detail: 'OCCRP Aleph · Politically Exposed'  },
  { label: 'Verifying India identity records', detail: 'PAN · Aadhaar · DL · Voter ID'      },
  { label: 'Scanning MCA21 corporate data',    detail: 'ROC filings · Directorship graph'   },
  { label: 'Probing social account handles',   detail: 'Email → Platform (holehe probe)'    },
  { label: 'Scoring adverse media signals',    detail: 'News OSINT · Financial press'       },
  { label: 'Computing unified risk score',     detail: 'FATF Rec.10 · Session 16 engine'    },
];

function ScanningOverlay({ seed, type }: { seed: string; type: 'CDD' | 'EDD' }) {
  const [step, setStep]   = useState(0);
  const [secs, setSecs]   = useState(0);

  useEffect(() => {
    const tick = setInterval(() => {
      setSecs(s => s + 1);
      setStep(v => Math.min(v + 1, SCAN_STEPS.length));
    }, 750);
    return () => clearInterval(tick);
  }, []);

  const pct = Math.min(10 + (step / SCAN_STEPS.length) * 82, 94);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(5,7,9,0.96)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(6px)',
      }}
    >
      {/* Animated scan line */}
      <motion.div
        style={{
          position: 'absolute', left: 0, right: 0, height: 1, pointerEvents: 'none',
          background: 'linear-gradient(90deg,transparent,#00C8FF55,#00C8FF,#00C8FF55,transparent)',
        }}
        animate={{ top: ['10%', '90%', '10%'] }}
        transition={{ duration: 3.5, repeat: Infinity, ease: 'linear' }}
      />

      <div
        style={{
          background: '#090D13',
          border: '1px solid #1E2B3F',
          boxShadow: '0 0 60px #00C8FF12',
          borderRadius: 16,
          padding: '32px 40px',
          width: '100%',
          maxWidth: 560,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Corner decorations */}
        {[['0','0','borderTop','borderLeft'],['0','auto','borderTop','borderRight'],
          ['auto','0','borderBottom','borderLeft'],['auto','auto','borderBottom','borderRight']
        ].map(([t,r,b1,b2], i) => (
          <div key={i} style={{
            position:'absolute', top: t as string, right: r as string,
            bottom: t === 'auto' ? '0' : undefined, left: r === 'auto' ? '0' : undefined,
            width: 12, height: 12,
            [b1]: '2px solid #00C8FF40', [b2]: '2px solid #00C8FF40',
          }} />
        ))}

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 24 }}>
          <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
            <div style={{ position:'relative', width:40, height:40, flexShrink:0 }}>
              <div style={{
                width:40, height:40, borderRadius:8,
                background:'#001A2E', border:'1px solid #00C8FF30',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                <Shield size={16} style={{ color:'#00C8FF' }} />
              </div>
              <motion.div
                style={{
                  position:'absolute', inset:-3, borderRadius:10,
                  border:'1.5px solid #00C8FF',
                }}
                animate={{ opacity:[0.8,0.1,0.8], scale:[1,1.08,1] }}
                transition={{ duration:1.6, repeat:Infinity }}
              />
            </div>
            <div>
              <p style={{ color:'#00C8FF', fontFamily:'monospace', fontWeight:800, fontSize:11, letterSpacing:'0.2em' }}>
                {type} PIPELINE ACTIVE
              </p>
              <p style={{ color:'#5A7A9A', fontFamily:'monospace', fontSize:9, marginTop:2 }}>
                {seed}
              </p>
            </div>
          </div>
          <div style={{
            background:'#00C8FF15', color:'#00C8FF', border:'1px solid #00C8FF30',
            fontFamily:'monospace', fontSize:11, fontWeight:800,
            padding:'4px 10px', borderRadius:6,
          }}>
            {secs}s
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ background:'#0F1928', borderRadius:4, height:4, marginBottom:24, overflow:'hidden' }}>
          <motion.div
            style={{
              height:4, borderRadius:4,
              background:'linear-gradient(90deg,#00C8FF,#2979FF)',
              boxShadow:'0 0 8px #00C8FF66',
            }}
            animate={{ width:`${pct}%` }}
            transition={{ duration:0.6, ease:'easeOut' }}
          />
        </div>

        {/* Steps */}
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {SCAN_STEPS.map((s, i) => {
            const done   = i < step - 1;
            const active = i === step - 1;
            const future = i >= step;
            return (
              <motion.div
                key={i}
                initial={{ opacity:0, x:-10 }}
                animate={i < step ? { opacity:1, x:0 } : {}}
                transition={{ duration:0.3 }}
                style={{ display:'flex', alignItems:'flex-start', gap:10 }}
              >
                {/* indicator */}
                <div style={{ width:16, flexShrink:0, display:'flex', justifyContent:'center', paddingTop:2 }}>
                  {done ? (
                    <CheckCircle2 size={12} style={{ color:'#00C85380' }} />
                  ) : active ? (
                    <motion.div
                      style={{ width:8, height:8, borderRadius:'50%', background:'#00C8FF', marginTop:2 }}
                      animate={{ opacity:[1,0.3,1], scale:[1,1.3,1] }}
                      transition={{ duration:0.8, repeat:Infinity }}
                    />
                  ) : (
                    <div style={{ width:8, height:8, borderRadius:'50%', background:'#182030', marginTop:2 }} />
                  )}
                </div>

                <div style={{ display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap' }}>
                  <span style={{
                    fontFamily:'monospace', fontSize:10,
                    color: active ? '#DCE8F5' : done ? '#5A7A9A' : '#283A50',
                    fontWeight: active ? 700 : 400,
                  }}>
                    {s.label}
                  </span>
                  {!future && (
                    <span style={{ fontFamily:'monospace', fontSize:8, color:'#283A50' }}>
                      {s.detail}
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer */}
        <p style={{ color:'#283A50', fontFamily:'monospace', fontSize:9, marginTop:20, textAlign:'center' }}>
          Typically 5–15 seconds · degraded sources are skipped automatically
        </p>
      </div>
    </motion.div>
  );
}


// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ProfilerIndex() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [seed, setSeed] = useState('');
  const [seedType, setSeedType] = useState<SeedType | 'AUTO'>('AUTO');
  const [jurisdiction, setJurisdiction] = useState('india_gulf');
  const [clientRef, setClientRef] = useState('');
  const [panError, setPanError] = useState('');

  const defaultModules = Object.fromEntries(MODULE_CONFIG.map((m) => [m.key, m.defaultOn]));
  const [modules, setModules] = useState<Record<string, boolean>>(defaultModules);

  const enabledModules = Object.entries(modules)
    .filter(([, v]) => v)
    .map(([k]) => k);

  function validatePan(value: string): boolean {
    if (seedType === 'PAN' && !PAN_RE.test(value.trim())) {
      setPanError('Invalid PAN format. Expected: AAAAA9999A');
      return false;
    }
    setPanError('');
    return true;
  }

  const resolvedSeedType: SeedType | undefined = seedType === 'AUTO' ? undefined : seedType;

  const cddMutation = useMutation({
    mutationFn: () =>
      profilerApi.runCDD({
        seed: seed.trim(),
        seed_type: resolvedSeedType,
        client_ref: clientRef.trim() || undefined,
        modules: enabledModules,
      }),
    onSuccess: (report) => {
      navigate(`/app/profiler/case/${report.case_id}/cdd`, { state: { report } });
    },
  });

  const eddMutation = useMutation({
    mutationFn: () =>
      profilerApi.runEDD({
        seed: seed.trim(),
        seed_type: resolvedSeedType,
        client_ref: clientRef.trim() || undefined,
      }),
    onSuccess: (result) => {
      navigate(`/app/profiler/case/${result.case_id}/edd`);
    },
  });

  const recentQuery = useQuery({
    queryKey: ['profiler-cases-recent'],
    queryFn: () => profilerApi.listCases({ limit: 8 }),
    staleTime: 60_000,
  });

  function handleRun(type: 'cdd' | 'edd') {
    if (!seed.trim()) return;
    if (!validatePan(seed.trim())) return;
    if (type === 'cdd') cddMutation.mutate();
    else eddMutation.mutate();
  }

  function handleModuleChange(key: string, val: boolean) {
    setModules((prev) => ({ ...prev, [key]: val }));
  }

  const anyLoading = cddMutation.isPending || eddMutation.isPending;

  const errorMsg =
    (cddMutation.error as Error)?.message ??
    (eddMutation.error as Error)?.message ??
    panError;

  return (
    <>
    <AnimatePresence>
      {anyLoading && (
        <ScanningOverlay seed={seed.trim()} type={cddMutation.isPending ? 'CDD' : 'EDD'} />
      )}
    </AnimatePresence>
    <div
      style={{ background: '#050709', minHeight: '100vh' }}
      className="flex flex-col"
    >
      {/* Header */}
      <div style={{ background: '#090D13', borderBottom: '1px solid #182030' }} className="px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-[13px] font-mono font-bold tracking-[0.22em]" style={{ color: '#DCE8F5' }}>
              PROFILER
            </h1>
            <p className="text-[10px] font-mono mt-0.5" style={{ color: '#5A7A9A' }}>
              AML Customer Due Diligence · Entity Intelligence
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Shield size={14} style={{ color: '#00C8FF' }} />
            {user && (
              <span className="text-[10px] font-mono" style={{ color: '#5A7A9A' }}>
                {user.email}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-6 flex flex-col gap-8">

        {/* ── Seed Input Form ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }}
          className="p-5"
        >
          {/* Main input */}
          <div className="relative mb-4">
            <Search
              size={16}
              style={{ color: '#5A7A9A' }}
              className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none"
            />
            <input
              type="text"
              value={seed}
              onChange={(e) => {
                setSeed(e.target.value);
                if (panError) validatePan(e.target.value);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleRun('cdd')}
              placeholder="Enter name, email, phone, PAN, company, domain or IP…"
              className="w-full pl-10 pr-4 py-3.5 rounded-lg font-mono text-sm outline-none transition-all"
              style={{
                background: '#0C1118',
                border: `1.5px solid ${errorMsg && !panError ? '#FF1F1F44' : '#1E2B3F'}`,
                color: '#DCE8F5',
                fontSize: 13,
                boxShadow: seed ? '0 0 0 2px #00C8FF18' : undefined,
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#00C8FF';
                e.currentTarget.style.boxShadow = '0 0 0 2px #00C8FF18';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#1E2B3F';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* PAN error */}
          {panError && (
            <p className="text-[10px] font-mono mb-3" style={{ color: '#FF1F1F' }}>
              {panError}
            </p>
          )}

          {/* Seed type pills */}
          <div className="flex flex-wrap gap-2 mb-3">
            {SEED_TYPES.map((s) => {
              const active = seedType === s.value;
              return (
                <button
                  key={s.value}
                  onClick={() => { setSeedType(s.value); if (s.value !== 'PAN') setPanError(''); }}
                  className="px-3 py-1 rounded-full font-mono text-[10px] font-bold tracking-wider transition-all"
                  style={{
                    background: active ? '#00C8FF18' : '#0C1118',
                    color: active ? '#00C8FF' : '#5A7A9A',
                    border: `1px solid ${active ? '#00C8FF44' : '#182030'}`,
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Jurisdiction + Client ref row */}
          <div className="flex flex-wrap gap-3 mb-5">
            <div className="flex gap-2 flex-wrap">
              {JURISDICTIONS.map((j) => {
                const active = jurisdiction === j.value;
                return (
                  <button
                    key={j.value}
                    onClick={() => setJurisdiction(j.value)}
                    className="px-3 py-1 rounded-lg font-mono text-[10px] transition-all"
                    style={{
                      background: active ? '#0F2030' : '#0C1118',
                      color: active ? '#DCE8F5' : '#3A5A7A',
                      border: `1px solid ${active ? '#1E3A5F' : '#182030'}`,
                    }}
                  >
                    {j.label}
                  </button>
                );
              })}
            </div>
            <input
              type="text"
              value={clientRef}
              onChange={(e) => setClientRef(e.target.value)}
              placeholder="Client ref (optional)"
              className="flex-1 min-w-[160px] px-3 py-1 rounded-lg font-mono text-[11px] outline-none"
              style={{
                background: '#0C1118',
                border: '1px solid #182030',
                color: '#DCE8F5',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#1E2B3F'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#182030'; }}
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 items-center flex-wrap">
            <button
              onClick={() => handleRun('cdd')}
              disabled={!seed.trim() || anyLoading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-mono text-[11px] font-bold tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: seed.trim() && !anyLoading ? '#00C8FF18' : '#0C1118',
                color: '#00C8FF',
                border: '1px solid #00C8FF44',
              }}
            >
              {cddMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <FileSearch size={13} />}
              RUN CDD
            </button>

            <button
              onClick={() => handleRun('edd')}
              disabled={!seed.trim() || anyLoading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-mono text-[11px] font-bold tracking-widest transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: seed.trim() && !anyLoading ? '#A855F718' : '#0C1118',
                color: '#A855F7',
                border: '1px solid #A855F744',
              }}
            >
              {eddMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : <Shield size={13} />}
              RUN EDD
            </button>

            {errorMsg && (
              <span className="text-[10px] font-mono" style={{ color: '#FF1F1F' }}>
                {errorMsg}
              </span>
            )}
          </div>
        </motion.div>

        {/* ── Module Toggle Grid ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.08 }}
          style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }}
          className="p-5"
        >
          <ModuleToggleGrid modules={modules} onChange={handleModuleChange} />
        </motion.div>

        {/* ── Verify shortcut ── */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.14 }}
        >
          <Link
            to="/app/profiler/verify"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#090D13', border: '1px solid #182030',
              borderRadius: 10, padding: '12px 18px',
              textDecoration: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Shield size={13} style={{ color: '#A855F7' }} />
              <div>
                <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>Identifier Verification</p>
                <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9, marginTop: 1 }}>NMC Doctor · PAN Card · GSTIN — direct government registry lookups</p>
              </div>
            </div>
            <ArrowUpRight size={12} style={{ color: '#5A7A9A', flexShrink: 0 }} />
          </Link>
        </motion.div>

        {/* ── Recent Cases ── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.16 }}
          style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }}
          className="overflow-hidden"
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{ borderBottom: '1px solid #182030' }}
          >
            <p className="text-[9px] font-mono tracking-[0.18em]" style={{ color: '#5A7A9A' }}>
              RECENT CASES
            </p>
            <Link
              to="/app/profiler/cases"
              className="flex items-center gap-1 text-[9px] font-mono transition-colors"
              style={{ color: '#5A7A9A' }}
            >
              View all <ArrowUpRight size={10} />
            </Link>
          </div>

          {/* Table */}
          {recentQuery.isLoading ? (
            <div>
              {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
            </div>
          ) : recentQuery.isError ? (
            <div className="px-5 py-8 text-center">
              <p className="text-[11px] font-mono" style={{ color: '#FF1F1F' }}>
                Failed to load cases.
              </p>
            </div>
          ) : (recentQuery.data?.cases ?? []).length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-[11px] font-mono" style={{ color: '#3A5A7A' }}>
                No cases yet. Run your first CDD above.
              </p>
            </div>
          ) : (
            <div>
              {/* Column header */}
              <div
                className="grid px-5 py-2"
                style={{
                  gridTemplateColumns: '1fr 80px 80px 60px 70px',
                  borderBottom: '1px solid #0F1520',
                }}
              >
                {['ENTITY', 'TYPE', 'TIER', 'CASE', 'WHEN'].map((h) => (
                  <span key={h} className="text-[8px] font-mono tracking-widest" style={{ color: '#3A5A7A' }}>
                    {h}
                  </span>
                ))}
              </div>

              {recentQuery.data?.cases.map((c: CDDReport) => {
                const isEDD = c.case_type === 'EDD';
                const href = `/app/profiler/case/${c.case_id}/${isEDD ? 'edd' : 'cdd'}`;
                const name = c.identifier_cluster?.canonical ?? c.seed ?? c.case_reference;
                return (
                  <div
                    key={c.case_id}
                    onClick={() => navigate(href)}
                    className="grid px-5 py-3 cursor-pointer transition-colors hover:bg-white/[0.03] items-center"
                    style={{
                      gridTemplateColumns: '1fr 80px 80px 60px 70px',
                      borderBottom: '1px solid #0F1520',
                    }}
                  >
                    <span className="font-mono text-[11px] truncate pr-3" style={{ color: '#DCE8F5' }}>
                      {name}
                    </span>
                    <SeedChip seedType={c.seed_type} />
                    <RiskTierBadge tier={c.risk_tier as RiskTier} />
                    <CaseTypeBadge type={c.case_type} />
                    <span className="flex items-center gap-1 text-[9px] font-mono" style={{ color: '#3A5A7A' }}>
                      <Clock size={9} />
                      {relativeTime(c.created_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
    </>
  );
}
